/*
 * Obermaier — header.js
 * <om-header>: solid-on-scroll state, mega panels (hover intent + click + keyboard).
 * <om-drilldown>: mobile menu levels.
 */
import { define, openSheet } from '@theme/core';

const desktop = matchMedia('(min-width: 990px)');
const canHover = matchMedia('(hover: hover)');

class OmHeader extends HTMLElement {
  connectedCallback() {
    this.triggers = [...this.querySelectorAll('.om-nav__link[aria-controls]')];
    this.openTrigger = null;
    this.timer = null;

    this.onScroll = this.onScroll.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onDocClick = this.onDocClick.bind(this);

    if (this.dataset.transparent === 'true') {
      addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    // The mega menu pictures load lazily; start loading them as soon as the visitor reaches for the
    // navigation, so a panel never opens with empty tiles.
    const warmUp = () => this.querySelectorAll('.om-mega img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    this.querySelector('.om-nav')?.addEventListener('pointerenter', warmUp, { once: true });
    this.querySelector('.om-nav')?.addEventListener('focusin', warmUp, { once: true });

    for (const trigger of this.triggers) {
      const item = trigger.closest('.om-nav__item');
      trigger.addEventListener('click', () => (this.openTrigger === trigger ? this.close() : this.open(trigger)));
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !canHover.matches) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.open(trigger), this.openTrigger ? 0 : 110);
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.close(), 180);
      });
    }

    // Non-menu nav items close an open panel when hovered
    this.querySelectorAll('.om-nav__item > a').forEach((a) =>
      a.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.close())
    );

    this.addEventListener('keydown', this.onKey);
    // Close when keyboard focus leaves the open panel (otherwise it would cover the focused element).
    this.addEventListener('focusout', (event) => {
      if (!this.openTrigger) return;
      const to = event.relatedTarget;
      if (to && this.openTrigger.closest('.om-nav__item').contains(to)) return;
      if (to && this.contains(to) && to.matches?.('.om-nav__link[aria-controls]')) return;
      this.close();
    });
    this.onDocKey = (event) => {
      if (event.key === 'Escape' && this.openTrigger && !this.contains(event.target)) this.close();
    };
    document.addEventListener('keydown', this.onDocKey);
    document.addEventListener('click', this.onDocClick);
    this.onMedia = () => this.close();
    desktop.addEventListener('change', this.onMedia);
    this.syncHeight(); // first value stays synchronous: product.js, collection.js and the bathtub page read --om-header-h
    // The bar height only changes across the breakpoint; observing it avoids a forced layout on every resize
    // (e.g. each time the mobile URL bar shows or hides).
    const bar = this.querySelector('.om-header__bar');
    this.barObserver = new ResizeObserver(() => this.syncHeight());
    if (bar) this.barObserver.observe(bar);

    // <shopify-account> waits in a <template> and is only mounted where it is visible (>=990 px).
    // Connected but hidden, it would still load its dialog, the login form and the Shop login code.
    this.onAccountMedia = () => {
      const template = desktop.matches && this.querySelector('template[data-om-account]');
      if (template) template.replaceWith(document.importNode(template.content, true));
    };
    this.onAccountMedia();
    desktop.addEventListener('change', this.onAccountMedia);

    // Theme editor: selecting a mega-menu block opens its panel, deselecting closes it.
    this.onBlockSelect = (event) => {
      const panel = this.querySelector(`.om-mega[data-block-id="${event.detail.blockId}"]`);
      if (!panel) return;
      const trigger = this.querySelector(`[aria-controls="${panel.id}"]`);
      if (trigger) this.open(trigger);
    };
    this.onBlockDeselect = (event) => {
      if (this.querySelector(`.om-mega[data-block-id="${event.detail.blockId}"]`)) this.close();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    removeEventListener('scroll', this.onScroll);
    this.barObserver?.disconnect();
    desktop.removeEventListener('change', this.onMedia);
    desktop.removeEventListener('change', this.onAccountMedia);
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKey);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  syncHeight() {
    const bar = this.querySelector('.om-header__bar');
    if (!bar) return;
    const h = bar.offsetHeight;
    if (h === this.lastH) return;
    this.lastH = h;
    document.documentElement.style.setProperty('--om-header-h', `${h}px`);
  }

  onScroll() {
    this.classList.toggle('is-solid', scrollY > 24 || !!this.openTrigger);
    this.syncTone();
  }

  syncTone() {
    const transparent = this.dataset.transparent === 'true' && !this.classList.contains('is-solid');
    if (transparent) this.dataset.tone = 'dark';
    else delete this.dataset.tone;
  }

  open(trigger) {
    if (this.openTrigger === trigger) return;
    if (this.openTrigger) this.panelFor(this.openTrigger).hidden = true;
    if (this.openTrigger) this.openTrigger.setAttribute('aria-expanded', 'false');
    this.openTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    this.panelFor(trigger).hidden = false;
    this.classList.add('is-mega-open', 'is-solid');
    this.syncTone();
  }

  close({ focus = false } = {}) {
    clearTimeout(this.timer);
    const trigger = this.openTrigger;
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', 'false');
    this.panelFor(trigger).hidden = true;
    this.openTrigger = null;
    this.classList.remove('is-mega-open');
    if (this.dataset.transparent === 'true') this.onScroll();
    if (focus) trigger.focus();
  }

  panelFor(trigger) {
    return this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
  }

  onKey(event) {
    if (event.key === 'Escape' && this.openTrigger) {
      event.preventDefault();
      this.close({ focus: true });
      return;
    }
    // Arrow keys move between top-level items
    const current = event.target.closest('.om-nav__link');
    if (!current || !['ArrowLeft', 'ArrowRight', 'ArrowDown'].includes(event.key)) return;
    const links = [...this.querySelectorAll('.om-nav__link')];
    const i = links.indexOf(current);
    if (event.key === 'ArrowDown' && current.hasAttribute('aria-controls')) {
      event.preventDefault();
      this.open(current);
      this.panelFor(current).querySelector('a')?.focus();
      return;
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const next = links[(i + (event.key === 'ArrowRight' ? 1 : -1) + links.length) % links.length];
      next.focus();
      if (this.openTrigger && next.hasAttribute('aria-controls')) this.open(next);
    }
  }

  onDocClick(event) {
    if (this.openTrigger && !this.contains(event.target)) this.close();
  }
}

const fold = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

class OmDrilldown extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-level="0"]');
    this.body = this.closest('.om-sheet__body');
    this.addEventListener('click', (event) => {
      // The search field at the top: close the menu first, then open the search sheet.
      if (event.target.closest('[data-menu-search]')) {
        const dialog = this.closest('dialog');
        dialog?.addEventListener('close', () => openSheet('om-search'), { once: true });
        dialog?.close();
        return;
      }
      const jump = event.target.closest('[data-brand-jump]');
      if (jump) {
        event.preventDefault();
        this.jumpTo(jump);
        return;
      }
      const drill = event.target.closest('[data-drill]');
      if (drill) {
        const panel = this.querySelector(`[data-panel="${drill.dataset.drill}"]`);
        if (!panel) return;
        this.lastDrill = drill;
        this.root.hidden = true;
        panel.hidden = false;
        panel.querySelector('[data-drill-back]')?.focus({ preventScroll: true });
        this.body?.scrollTo({ top: 0 });
        return;
      }
      if (event.target.closest('[data-drill-back]')) this.reset({ focus: true });
    });
    this.closest('dialog')?.addEventListener('close', () => this.reset());

    // Brands: filter field and A–Z index.
    this.brands = this.querySelector('[data-brand-index]');
    if (this.brands) {
      this.brandItems = [...this.brands.querySelectorAll('[data-brand]')].map((li) => ({ li, key: fold(li.textContent) }));
      this.brandFilter = this.brands.querySelector('[data-brand-filter]');
      this.brandFilter.addEventListener('input', () => this.filterBrands());
      this.brandFilter.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        // Enter opens the brand when exactly one matches.
        const hits = this.brandItems.filter(({ li }) => !li.hidden);
        if (hits.length === 1) hits[0].li.querySelector('a')?.click();
      });
      this.body?.addEventListener('scroll', () => this.markLetter(), { passive: true });
    }
  }

  filterBrands() {
    const query = fold(this.brandFilter.value);
    const visible = new Set();
    for (const { li, key } of this.brandItems) {
      li.hidden = query !== '' && !key.includes(query);
      if (!li.hidden) visible.add(li.dataset.letterOf);
    }
    this.brands.querySelectorAll('[data-letter]').forEach((heading) => (heading.hidden = !visible.has(heading.dataset.letter)));
    this.brands.querySelectorAll('[data-brand-jump]').forEach((link) => link.classList.toggle('is-off', !visible.has(link.dataset.brandJump)));
    this.brands.querySelector('[data-brand-empty]').hidden = visible.size > 0;
  }

  jumpTo(link) {
    const heading = this.brands.querySelector(`[data-letter="${CSS.escape(link.dataset.brandJump)}"]:not([hidden])`);
    if (!heading || !this.body) return;
    const tools = this.brands.querySelector('.om-menu__brand-tools');
    const top = heading.getBoundingClientRect().top - this.body.getBoundingClientRect().top + this.body.scrollTop - tools.offsetHeight;
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.body.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
  }

  // Highlights the letter whose brands are at the top of the list.
  markLetter() {
    if (this.brands.closest('[data-panel]').hidden) return;
    const tools = this.brands.querySelector('.om-menu__brand-tools');
    const line = tools.getBoundingClientRect().bottom + 4;
    let current = null;
    for (const heading of this.brands.querySelectorAll('[data-letter]:not([hidden])')) {
      if (heading.getBoundingClientRect().top > line) break;
      current = heading.dataset.letter;
    }
    this.brands.querySelectorAll('[data-brand-jump]').forEach((link) => {
      if (link.dataset.brandJump === current) {
        if (!link.hasAttribute('aria-current')) {
          link.setAttribute('aria-current', 'true');
          const row = link.parentElement;
          row.scrollTo({ left: link.offsetLeft - (row.clientWidth - link.offsetWidth) / 2 });
        }
      } else link.removeAttribute('aria-current');
    });
  }

  reset({ focus = false } = {}) {
    this.querySelectorAll('[data-panel]').forEach((p) => (p.hidden = true));
    this.root.hidden = false;
    if (this.brandFilter?.value) {
      this.brandFilter.value = '';
      this.filterBrands();
    }
    if (focus) this.lastDrill?.focus({ preventScroll: true });
  }
}

define('om-header', OmHeader);
define('om-drilldown', OmDrilldown);
